import React, { useRef, useContext, useState, useEffect, useCallback } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Star, Hammer, Palette, Shield, ChevronLeft, ChevronRight } from 'lucide-react';
import { AppContext } from '../AppContext';
import './AboutPage.css';

const AboutPage = () => {
  const { lang } = useContext(AppContext);
  const isMr = lang === 'mr';

  const milestones = [
    { year: '1995', title: isMr ? 'सुरुवात' : 'The Genesis', desc: isMr ? 'गोविंद राणे यांनी महाराष्ट्रात एका वर्कबेंचसह आणि शुद्ध सोन्याच्या कलाकुसरीच्या दृष्टिकोनातून जीडीआर ज्वेलर्सची स्थापना केली.' : 'Govind Rane founded GDR Jewellers in Maharashtra with a single workbench and a vision for purest gold craftsmanship.' },
    { year: '2005', title: isMr ? 'वारशाचा विस्तार' : 'Legacy Expansion', desc: isMr ? 'पारंपारिक हाताने बनवलेले दागिन्यांचे डिझाइन आणि शुद्ध चांदीच्या पैंजणांसाठी आमचे मुख्य शोरूम उघडले.' : 'Opened our flagship showroom specializing in custom handcrafted temple jewellery and pure silver payals.' },
    { year: '2015', title: isMr ? 'डिजिटल युग' : 'Digital Era', desc: isMr ? '3D ज्वेलरी CAD डिझायनिंग सादर केले, ज्यामुळे ग्राहकांना त्यांचे डिझाइन पाहता येते.' : 'Introduced 3D jewellery CAD designing, allowing clients to visualize their bespoke creations before crafting begins.' },
    { year: '2026', title: isMr ? 'नवीन संग्रह' : 'New Collections', desc: isMr ? 'आधुनिक पिढीसाठी डिझाइन केलेले प्रीमियम लाइटवेट सोन्याचे आणि चांदीचे दागिने लाँच केले.' : 'Launched premium lightweight daily-wear gold & silver lines crafted for the modern generation.' }
  ];

  const values = [
    { icon: <Star size={28} />, title: isMr ? 'पवित्रता प्रथम' : 'Purity First', desc: isMr ? 'प्रत्येक तुकडा BIS हॉलमार्क केलेला आहे, ज्यामुळे 100% शुद्धता निश्चित होते.' : 'Every piece is BIS Hallmarked, ensuring 100% certified gold and sterling silver purity standards.' },
    { icon: <Hammer size={28} />, title: isMr ? 'वारसा कला' : 'Heritage Craft', desc: isMr ? 'तीन दशकांचा पारंपारिक वारसा आणि हस्तकलेचा अनुभव.' : 'Three decades of passing down traditional filigree, meenakari, and stone-setting techniques through generations.' },
    { icon: <Palette size={28} />, title: isMr ? 'सानुकूल डिझाइन' : 'Bespoke Design', desc: isMr ? 'तुमची कल्पना आणि आमची कलाकुसर एकत्र करून वैयक्तिकृत दागिने.' : 'Personalized jewellery consultations where your imagination and our craftsmanship unite.' },
    { icon: <Shield size={28} />, title: isMr ? 'आजीवन काळजी' : 'Lifetime Care', desc: isMr ? 'तुमचे दागिने कायम नवीन ठेवण्यासाठी मोफत पॉलिशिंग, आकार देणे आणि दुरुस्ती.' : 'Complimentary polishing, sizing, and repair services to keep your jewellery looking eternally new.' }
  ];

  const craftSteps = [
    { step: '01', title: isMr ? 'डिझाइन आणि सल्लामसलत' : 'Design & Consultation', desc: isMr ? 'आम्ही तुमची कल्पना, प्रसंग आणि वैयक्तिक शैली समजून घेण्यासाठी सखोल चर्चा सुरू करतो.' : 'We begin with an in-depth discussion to understand your vision, occasion, and personal style.' },
    { step: '02', title: isMr ? 'मेणाचे मॉडेलिंग' : 'Wax Modelling', desc: isMr ? 'आमचे कारागीर एक अचूक मेणाचे मॉडेल तयार करतात, ज्यामुळे तुम्हाला अंतिम डिझाइन पाहता येते.' : 'Our craftsmen create a precise wax model, allowing you to visualize the final piece.' },
    { step: '03', title: isMr ? 'मेटल कास्टिंग' : 'Metal Casting', desc: isMr ? 'कास्टिंग तंत्राचा वापर करून, तुमचे डिझाइन सर्वात शुद्ध धातूंमध्ये तयार केले जाते.' : 'Using the lost-wax casting technique, your design is cast in the purest certified metals.' },
    { step: '04', title: isMr ? 'खडे बसवणे' : 'Stone Setting', desc: isMr ? 'कुशल कारागीर प्रत्येक खडा हाताने बसवतात, अचूकता आणि चमक सुनिश्चित करतात.' : 'Master gem-setters place each stone by hand, ensuring perfect alignment and brilliance.' },
    { step: '05', title: isMr ? 'पॉलिश आणि हॉलमार्क' : 'Polish & Hallmark', desc: isMr ? 'दागिने मल्टी-स्टेज पॉलिशिंगमधून जातात आणि त्यांना BIS हॉलमार्क प्रमाणपत्र मिळते.' : 'The piece undergoes multi-stage polishing and receives its official BIS Hallmark certification.' }
  ];

  const team = [
    { name: isMr ? 'गोविंद देवराम राणे' : 'Govind Devram Rane', role: isMr ? 'संस्थापक आणि मुख्य कारागीर' : 'Founder & Master Craftsman', initials: 'GR' },
    { name: isMr ? 'विजय गोविंद राणे' : 'Vijay Govind Rane', role: isMr ? 'डिझाइन प्रमुख' : 'Head of Design & Gemology', initials: 'VR' },
    { name: isMr ? 'राहुल गोविंद राणे' : 'Rahul Govind Rane', role: isMr ? 'ऑपरेशन्स आणि ग्राहक संबंध' : 'Operations & Customer Relations', initials: 'RR' }
  ];

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const timelineRef = useRef(null);
  const { scrollYProgress: tlProgress } = useScroll({
    target: timelineRef,
    offset: ['start 75%', 'end 70%']
  });
  const lineProgressHeight = useTransform(tlProgress, [0, 1], ['0%', '100%']);

  const stepsTrackRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const scrollTimeoutRef = useRef(null);

  const getCardDistance = useCallback(() => {
    if (!stepsTrackRef.current) return 272;
    const cards = stepsTrackRef.current.querySelectorAll('.craft-step-card');
    if (cards.length >= 2) {
      const dist = cards[1].offsetLeft - cards[0].offsetLeft;
      if (dist > 0) return dist;
    }
    return 272;
  }, []);

  // Position at the middle set on initial load for circular looping
  useEffect(() => {
    const track = stepsTrackRef.current;
    if (!track) return;

    const initPos = () => {
      const dist = getCardDistance();
      const setWidth = dist * craftSteps.length;
      track.style.scrollBehavior = 'auto';
      track.scrollLeft = setWidth;
      track.style.scrollBehavior = 'smooth';
    };

    initPos();
    const t = setTimeout(initPos, 250);
    return () => clearTimeout(t);
  }, [craftSteps.length, getCardDistance]);

  // Infinite circular scroll handler (normalizes position invisibly)
  useEffect(() => {
    const track = stepsTrackRef.current;
    if (!track) return;

    const onScroll = () => {
      const dist = getCardDistance();
      const setWidth = dist * craftSteps.length;

      // Calculate which step is active (0 to 4)
      const currentScroll = track.scrollLeft;
      const normalizedScroll = ((currentScroll % setWidth) + setWidth) % setWidth;
      const idx = Math.round(normalizedScroll / dist) % craftSteps.length;
      setActiveIndex(idx);

      // Seamless infinite reset when scroll settles
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => {
        if (!track) return;
        const currentDist = getCardDistance();
        const currentSetWidth = currentDist * craftSteps.length;

        // Scrolled into 3rd set -> reset silently to 2nd set
        if (track.scrollLeft >= currentSetWidth * 2 - 10) {
          track.style.scrollBehavior = 'auto';
          track.scrollLeft -= currentSetWidth;
          track.style.scrollBehavior = 'smooth';
        }
        // Scrolled into 1st set -> reset silently to 2nd set
        else if (track.scrollLeft <= currentSetWidth * 0.4) {
          track.style.scrollBehavior = 'auto';
          track.scrollLeft += currentSetWidth;
          track.style.scrollBehavior = 'smooth';
        }
      }, 150);
    };

    track.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      track.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      clearTimeout(scrollTimeoutRef.current);
    };
  }, [craftSteps.length, getCardDistance]);

  // Continuous circular auto-slide: moves forward in an endless loop
  useEffect(() => {
    if (isHovered) return;

    const interval = setInterval(() => {
      const track = stepsTrackRef.current;
      if (!track) return;
      const dist = getCardDistance();
      track.scrollBy({ left: dist, behavior: 'smooth' });
    }, 3200);

    return () => clearInterval(interval);
  }, [isHovered, getCardDistance]);

  const handleScroll = (direction) => {
    const track = stepsTrackRef.current;
    if (!track) return;
    const dist = getCardDistance();
    track.scrollBy({
      left: direction === 'right' ? dist : -dist,
      behavior: 'smooth'
    });
  };

  const goToStep = (idx) => {
    const track = stepsTrackRef.current;
    if (!track) return;
    const dist = getCardDistance();
    const setWidth = dist * craftSteps.length;
    track.scrollTo({
      left: setWidth + idx * dist,
      behavior: 'smooth'
    });
    setActiveIndex(idx);
  };

  return (
    <div className="about-page-theme">
      {/* Hero with Parallax */}
      <section className="about-hero-section" ref={heroRef}>
        <motion.div className="about-hero-bg" style={{ y: heroY }}>
          <img src="/images/about-hero.jpg" alt="GDR Jewellery About Banner" />
          <div className="hero-gradient-overlay"></div>
        </motion.div>
        <motion.div className="about-hero-text" style={{ opacity: heroOpacity }}>
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {isMr ? 'जेथे सोने ' : 'Where Gold Meets '} <span className="text-gold">{isMr ? 'कलेशी' : 'Artistry'}</span> {isMr ? 'भेटते' : ''}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {isMr ? 'प्रत्येक दागिन्याची एक कहाणी असते. जीडीआर मध्ये, आम्ही त्या कहाण्या सोने, चांदी आणि मौल्यवान खड्यांमध्ये लिहितो.' : 'Every piece of jewellery tells a story. At GDR, we write those stories in gold, silver, and precious stones.'}
          </motion.p>
        </motion.div>
      </section>

      {/* Philosophy */}
      <section className="philosophy-wrap">
        <div className="container-max">
          <div className="philosophy-grid-theme">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="philosophy-text-col"
            >
              <span className="section-label-gold">{isMr ? 'आमचे तत्त्वज्ञान' : 'Our Philosophy'}</span>
              <h2>{isMr ? 'हस्तकलमध्ये शुद्धता, प्रत्येक नात्यात प्रामाणिकपणा' : 'Purity in Craft, Integrity in Every Relation'}</h2>
              <p>{isMr ? 'तीन दशकांहून अधिक काळ, जीडीआर ज्वेलर्स विश्वास आणि गुणवत्तेचे प्रतीक बनले आहे. आमचा असा विश्वास आहे की दागिने केवळ एक सजावटीचा प्रकार नाही - हा एक वारसा आहे, जपलेल्या क्षणांचा उत्सव आहे आणि पिढ्यानपिढ्या जतन केलेली एक स्मृती आहे.' : 'For over three decades, GDR Jewellers has stood as a beacon of trust and quality. We believe jewellery is not just a decorative accessory — it is an heirloom, a celebration of cherished moments, and a tangible memory passed across generations.'}</p>
              <p>{isMr ? '100% हॉलमार्क सोने, नैतिकदृष्ट्या मिळवलेले खडे आणि शुद्ध चांदीबद्दलची आमची बांधिलकी अटळ आहे. प्रत्येक डिझाइन तुमच्यापर्यंत पोहोचण्यापूर्वी अनेक गुणवत्ता तपासण्यांमधून जाते.' : 'Our commitment to 100% Hallmarked gold, ethically sourced stones, and pure chaste silver is unwavering. Every design goes through multiple quality checks before finding its rightful place on you.'}</p>
              <div className="stat-pills">
                <div className="stat-pill"><span>30+</span> {isMr ? 'वर्षे' : 'Years'}</div>
                <div className="stat-pill"><span>10k+</span> {isMr ? 'कुटुंबे जोडली' : 'Families Served'}</div>
                <div className="stat-pill"><span>100%</span> {isMr ? 'हॉलमार्क' : 'Hallmarked'}</div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="philosophy-img-col"
            >
              <div className="double-img-frame">
                <img src="images/painjan10.jpg" alt="Craftsmanship" className="img-main" />
                <img src="images/w ring1.jpg" alt="Gold Ring" className="img-float" />
                <div className="frame-deco"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="values-section">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="section-header-centered"
          >
            <span className="section-label-gold">{isMr ? 'आमची वैशिष्ट्ये' : 'What Defines Us'}</span>
            <h2>{isMr ? 'जीडीआर चे चार स्तंभ' : 'The Four Pillars of GDR'}</h2>
          </motion.div>

          <div className="values-grid">
            {values.map((v, i) => (
              <motion.div
                key={v.title}
                className="value-card"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                whileHover={{ y: -8 }}
              >
                <span className="value-icon">{v.icon}</span>
                <h3>{v.title}</h3>
                <p>{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Craft Journey */}
      <section className="craft-journey-section">
        <div className="craft-header">
          <span className="section-label-gold">{isMr ? 'प्रत्येक मास्टरपीसच्या मागे' : 'Behind Every Masterpiece'}</span>
          <h2>{isMr ? 'जीडीआर च्या निर्मितीचा प्रवास' : 'The Journey of a GDR Creation'}</h2>
          <p>{isMr ? 'कल्पनेपासून ते तुमच्या मनगटापर्यंत - आमच्या हस्तकलेचे पाच टप्पे' : 'From imagination to your wrist — the five stages of our artisanal process'}</p>
        </div>

        <div
          className="craft-slider-wrapper"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onTouchStart={() => setIsHovered(true)}
          onTouchEnd={() => {
            setTimeout(() => setIsHovered(false), 2500);
          }}
        >
          <button
            type="button"
            className="craft-nav-btn craft-nav-prev"
            onClick={() => handleScroll('left')}
            aria-label={isMr ? 'मागील टप्पा' : 'Previous step'}
          >
            <ChevronLeft size={24} />
          </button>

          <div className="craft-steps-track" ref={stepsTrackRef}>
            {[...craftSteps, ...craftSteps, ...craftSteps].map((step, i) => (
              <div
                key={`${step.step}-${i}`}
                className={`craft-step-card ${activeIndex === (i % craftSteps.length) ? 'is-active' : ''}`}
                onClick={() => goToStep(i % craftSteps.length)}
              >
                <div className="step-number">{step.step}</div>
                <div className="step-content">
                  <h4>{step.title}</h4>
                  <p>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            className="craft-nav-btn craft-nav-next"
            onClick={() => handleScroll('right')}
            aria-label={isMr ? 'पुढील टप्पा' : 'Next step'}
          >
            <ChevronRight size={24} />
          </button>
        </div>
      </section>

      {/* Timeline */}
      <section className="timeline-wrap" ref={timelineRef}>
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="section-header-centered"
          >
            <span className="section-label-gold">{isMr ? 'आमची कथा' : 'Our Story'}</span>
            <h2>{isMr ? 'काळाच्या ओघातील प्रवास' : 'A Journey Through Time'}</h2>
          </motion.div>

          <div className="timeline-track">
            {/* Base timeline line */}
            <div className="tl-center-line"></div>
            {/* Scroll-driven animated golden progress line */}
            <motion.div
              className="tl-center-line-fill"
              style={{ height: lineProgressHeight }}
            ></motion.div>

            {milestones.map((m, i) => {
              const isLeft = i % 2 === 0;
              return (
                <motion.div
                  key={m.year}
                  className={`tl-item ${isLeft ? 'tl-left' : 'tl-right'}`}
                  initial={{ opacity: 0, x: isLeft ? -70 : 70, y: 40 }}
                  whileInView={{ opacity: 1, x: 0, y: 0 }}
                  viewport={{ amount: 0.35, once: false }}
                  transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div className="tl-line-dot">
                    <span className="tl-dot-ping"></span>
                    <span className="tl-dot-point"></span>
                  </div>

                  <div className="tl-year-header">
                    <span className="tl-year">{m.year}</span>
                    <span className="tl-phase-pill">{isMr ? `टप्पा ०${i + 1}` : `Phase 0${i + 1}`}</span>
                  </div>

                  <motion.div
                    className="tl-panel"
                    whileHover={{ y: -6, scale: 1.01 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h3>{m.title}</h3>
                    <p>{m.desc}</p>
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Founders */}
      <section className="founders-wrap">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="section-header-centered"
          >
            <span className="section-label-gold">{isMr ? 'कुटुंबाला भेटा' : 'Meet the Family'}</span>
            <h2>{isMr ? 'जीडीआर शुद्धतेचे रक्षक' : 'The Guardians of GDR Purity'}</h2>
          </motion.div>

          <div className="founders-grid-theme">
            {team.map((t, i) => (
              <motion.div
                key={t.name}
                className="founder-card-theme"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
                whileHover={{ y: -10 }}
              >
                <div className="founder-avatar">
                  <span>{t.initials}</span>
                  <div className="avatar-ring"></div>
                </div>
                <h3>{t.name}</h3>
                <span className="founder-role">{t.role}</span>
                <div className="founder-separator"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Strip */}
      <section className="about-cta">
        <div className="about-cta-bg">
          <div className="cta-content">
            <h2>{isMr ? 'तुमचा परिपूर्ण दागिना शोधण्यासाठी तयार आहात?' : 'Ready to Find Your Perfect Piece?'}</h2>
            <p>{isMr ? 'आमच्या शोरूमला भेट द्या किंवा आमचे ऑनलाइन संग्रह एक्सप्लोर करा' : 'Visit our showroom or explore our curated collections online'}</p>
            <div className="cta-buttons">
              <a href="#/products" className="btn-cta-primary">{isMr ? 'संग्रह एक्सप्लोर करा' : 'Explore Collections'}</a>
              <a href="#/contact" className="btn-cta-outline">{isMr ? 'सल्लामसलत बुक करा' : 'Book a Consultation'}</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
